import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  formatMonthYear,
  getWeeksInMonth,
  getWeekDates,
  getCurrentWeekId,
} from "@/lib/week-utils";
import type { WeekSummary } from "@/lib/api";
import type { DayStatus } from "@/lib/plan-status";

const DAY_HEADERS = ["L", "M", "X", "J", "V", "S", "D"];

interface WeekCalendarPickerProps {
  weekList: WeekSummary[];
  selectedWeekId: string | null;
  /** The week the user is currently viewing (route week) — shown as the "target" */
  targetWeekId?: string;
  onSelectWeek: (weekId: string) => void;
}

export function WeekCalendarPicker({
  weekList,
  selectedWeekId,
  targetWeekId,
  onSelectWeek,
}: WeekCalendarPickerProps) {
  // Start on the month that contains the target week (so the user sees "where they are")
  const targetDate = targetWeekId ? new Date(targetWeekId + "T00:00:00") : new Date();
  const [year, setYear] = useState(targetDate.getFullYear());
  const [month, setMonth] = useState(targetDate.getMonth());

  const currentWeekId = getCurrentWeekId();

  const weekSummaryMap = useMemo(
    () => new Map(weekList.map((w) => [w.weekId, w])),
    [weekList],
  );

  const weeks = useMemo(() => getWeeksInMonth(year, month), [year, month]);

  function prevMonth() {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  }

  function nextMonth() {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={prevMonth} className="h-7 px-2">
          &larr;
        </Button>
        <h3 className="text-sm font-semibold">{formatMonthYear(year, month)}</h3>
        <Button variant="ghost" size="sm" onClick={nextMonth} className="h-7 px-2">
          &rarr;
        </Button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-px text-center">
        {DAY_HEADERS.map((d) => (
          <div key={d} className="text-[10px] font-medium text-muted-foreground py-0.5">
            {d}
          </div>
        ))}
      </div>

      {/* Week rows */}
      <div className="space-y-0.5">
        {weeks.map((weekId) => {
          const days = getWeekDates(weekId);
          const summary = weekSummaryMap.get(weekId);
          const weekStatus = summary?.status ?? "not_planned";
          const isCurrent = weekId === currentWeekId;
          const isSelected = weekId === selectedWeekId;
          const isTarget = weekId === targetWeekId;
          const hasData = weekStatus !== "not_planned";

          return (
            <div key={weekId}>
              <button
                onClick={() => onSelectWeek(weekId)}
                className={`grid grid-cols-7 gap-px w-full rounded-md p-0.5 transition-colors ${
                  isSelected
                    ? "bg-primary/20 ring-2 ring-primary"
                    : isTarget
                      ? "bg-amber-50 ring-1 ring-amber-400"
                      : isCurrent
                        ? "bg-primary/10 hover:bg-primary/15 ring-1 ring-primary/30"
                        : hasData
                          ? "bg-green-50 hover:bg-green-100"
                          : "bg-muted/30 hover:bg-muted/60"
                }`}
              >
              {days.map(({ day, date, dayNum }) => {
                const isInMonth =
                  new Date(date + "T00:00:00").getMonth() === month;
                const isToday = date === formatToday();
                const dayStatus: DayStatus = summary?.dayStatuses[day]?.status ?? "not_planned";

                return (
                  <div
                    key={date}
                    className={`text-center py-1 text-xs rounded relative ${
                      isToday
                        ? "bg-primary text-primary-foreground font-bold"
                        : isInMonth
                          ? "text-foreground"
                          : "text-muted-foreground/40"
                    }`}
                  >
                    {dayNum}
                    {summary && dayStatus !== "not_planned" && (
                      <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 size-0.5 rounded-full ${
                        dayStatus === "planned"
                          ? "bg-green-500"
                          : "bg-amber-500"
                      }`} />
                    )}
                  </div>
                );
              })}
              </button>
              {isTarget && (
                <p className="text-[9px] font-medium text-amber-600 text-center -mt-0.5 mb-0.5">
                  semana actual (destino)
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
